import { filter } from "lodash";
import { useEffect, useState } from "react";
import {
  Card,
  Table,
  Stack,
  TableRow,
  TableBody,
  TableCell,
  Container,
  Typography,
  TableContainer,
  TablePagination,
  Chip,
  IconButton,
  Menu,
  MenuItem,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
} from "@mui/material";
import Page from "../../../components/Page";
import Scrollbar from "../../../components/Scrollbar";
import SearchNotFound from "../../../components/SearchNotFound";
import { UserListHead, UserListToolbar } from "../user";
import SyncLoader from "react-spinners/SyncLoader";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import MoreVertIcon from "@mui/icons-material/MoreVert";

const TABLE_HEAD = [
  { id: "sno", label: "S.No", alignRight: false },
  { id: "full_name", label: "Full Name", alignRight: false },
  { id: "email", label: "Email", alignRight: false },
  { id: "subject", label: "Subject", alignRight: false },
  { id: "message", label: "Message", alignRight: false },
  { id: "created_at", label: "Created At", alignRight: false },
  { id: "action", label: "Action", alignRight: false },
];

function descendingComparator(a, b, orderBy) {
  if (b[orderBy] < a[orderBy]) {
    return -1;
  }
  if (b[orderBy] > a[orderBy]) {
    return 1;
  }
  return 0;
}

function getComparator(order, orderBy) {
  return order === "desc"
    ? (a, b) => descendingComparator(a, b, orderBy)
    : (a, b) => -descendingComparator(a, b, orderBy);
}

function applySortFilter(array, comparator, query) {
  const stabilizedThis = array?.map((el, index) => [el, index]);
  stabilizedThis.sort((a, b) => {
    const order = comparator(a[0], b[0]);
    if (order !== 0) return order;
    return a[1] - b[1];
  });
  if (query) {
    return filter(
      array,
      (_contact) => 
        _contact.full_name.toLowerCase().indexOf(query.toLowerCase()) !== -1 ||
        _contact.email.toLowerCase().indexOf(query.toLowerCase()) !== -1 ||
        _contact.subject.toLowerCase().indexOf(query.toLowerCase()) !== -1
    );
  }
  return stabilizedThis?.map((el) => el[0]);
}

const ContactUsMessages = () => {
  const detailObject = JSON.parse(localStorage.getItem("detailObject"));
  const accessArr = detailObject?.access_level?.split(',');
  const role = localStorage.getItem('role');
  
  const [page, setPage] = useState(0);
  const [order, setOrder] = useState("desc");
  const [selected, setSelected] = useState([]);
  const [orderBy, setOrderBy] = useState("created_at");
  const [filterName, setFilterName] = useState("");
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [contacts, setContacts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [anchorEl, setAnchorEl] = useState(null);
  const [selectedRow, setSelectedRow] = useState(null);
  const [viewDialogData, setViewDialogData] = useState(null);
  const [openDialog, setOpenDialog] = useState(false);
  const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
  const [userSorted, setUserSorted] = useState(false);

  useEffect(() => {
    fetchContactReports();
  }, []);

  const fetchContactReports = async () => {
    try {
      const token = localStorage.getItem("token");
      const response = await fetch(
        `${process.env.REACT_APP_LIVE_URL_NEW}/admin/get-contact-reports`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
        }
      );
      const responseJson = await response.json();
      
      if (responseJson.response_code === 200) {
        setContacts(responseJson.data || []);
      } else {
        toast.error("Failed to fetch contact reports");
      }
    } catch (error) {
      toast.error("Error fetching contact reports");
      console.error("Error:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleRequestSort = (event, property) => {
    setUserSorted(true);
    const isAsc = orderBy === property && order === "asc";
    setOrder(isAsc ? "desc" : "asc");
    setOrderBy(property);
  };

  const handleSelectAllClick = (event) => {
    if (event.target.checked) {
      const newSelecteds = contacts?.map((n) => n.id);
      setSelected(newSelecteds);
      return;
    }
    setSelected([]);
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleFilterByName = (event) => {
    setFilterName(event.target.value);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
    setSelectedRow(null);
  };

  const handleViewMessage = () => {
    console.log('Selected Row for View:', selectedRow);
    setViewDialogData(selectedRow);
    setOpenDialog(true);
    handleMenuClose();
  };

  const handleDeleteConfirm = () => {
    setOpenDeleteDialog(true);
    handleMenuClose();
  };

  const handleDelete = async () => {
    try {
      const token = localStorage.getItem("token");
      const response = await fetch(
        `${process.env.REACT_APP_LIVE_URL_NEW}/admin/delete-contact-report/${selectedRow?.id}`,
        {
          method: "DELETE",
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      if (response.ok) {
        toast.success("Contact message deleted successfully");
        fetchContactReports(); // Refresh the list
      } else {
        toast.error("Failed to delete contact message");
      }
    } catch (error) {
      toast.error("Error deleting contact message");
      console.error("Error:", error);
    }
    setOpenDeleteDialog(false);
    setSelectedRow(null);
  };

  const emptyRows = page > 0 ? Math.max(0, (1 + page) * rowsPerPage - contacts?.length) : 0;
  const filteredContacts = userSorted
    ? applySortFilter(contacts || [], getComparator(order, orderBy), filterName)
    : filter(
        contacts || [],
        (_contact) =>
          _contact.full_name.toLowerCase().indexOf(filterName.toLowerCase()) !== -1 ||
          _contact.email.toLowerCase().indexOf(filterName.toLowerCase()) !== -1 ||
          _contact.subject.toLowerCase().indexOf(filterName.toLowerCase()) !== -1
      );
  const isContactNotFound = filteredContacts.length === 0;

  const truncateText = (text, maxLength = 50) => {
    if (!text) return "";
    return text.length > maxLength ? `${text.substring(0, maxLength)}...` : text;
  };

  return (
    <>
      {loading ? (
        <Stack justifyContent="center" alignItems="center" sx={{ height: "60vh" }}>
          <SyncLoader color="#8080ff" loading={loading} size={20} />
        </Stack>
      ) : (
        <Page title="Contact Us Messages">
          <Container sx={{ position: "absolute", minWidth: "100%" }}>
            <Stack direction="row" alignItems="center" justifyContent="space-between" mb={3}>
              <Typography variant="h4" gutterBottom>
                Contact Us Messages
              </Typography>
            </Stack>
            <Card>
              <Stack
                pr={3}
                direction="row"
                justifyContent="space-between"
                alignItems="center"
              >
                <UserListToolbar
                  numSelected={selected.length}
                  filterName={filterName}
                  onFilterName={handleFilterByName}
                  placeholder="Search by name, email, or subject..."
                />
              </Stack>

              <Scrollbar>
                <TableContainer sx={{ minWidth: 1200 }}>
                  <Table>
                    <UserListHead
                      order={order}
                      orderBy={orderBy}
                      headLabel={TABLE_HEAD}
                      rowCount={contacts?.length}
                      numSelected={selected.length}
                      onRequestSort={handleRequestSort}
                      onSelectAllClick={handleSelectAllClick}
                    />
                    <TableBody>
                      {filteredContacts
                        .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                        ?.map((row, index) => {
                          const { id, full_name, email, subject, message, created_at } = row;
                          const isItemSelected = selected.indexOf(id) !== -1;

                          return (
                            <TableRow
                              hover
                              key={id}
                              tabIndex={-1}
                              role="checkbox"
                              selected={isItemSelected}
                              aria-checked={isItemSelected}
                            >
                              <TableCell>
                                <Typography variant="subtitle2">
                                  {index + 1 + rowsPerPage * page}
                                </Typography>
                              </TableCell>

                              <TableCell component="th" scope="row">
                                <Typography variant="subtitle2" noWrap>
                                  {full_name}
                                </Typography>
                              </TableCell>

                              <TableCell>
                                <Typography variant="body2" color="text.secondary">
                                  {email}
                                </Typography>
                              </TableCell>

                              <TableCell sx={{ maxWidth: 250 }}>
                                <Typography variant="body2" title={subject}>
                                  {truncateText(subject, 40)}
                                </Typography>
                              </TableCell>

                              <TableCell sx={{ maxWidth: 300 }}>
                                <Typography variant="body2" color="text.secondary" title={message}>
                                  {truncateText(message, 60)}
                                </Typography>
                              </TableCell>

                              <TableCell>
                                <Typography variant="body2" color="text.secondary">
                                  {created_at}
                                </Typography>
                              </TableCell>

                              <TableCell>
                                <IconButton
                                  onClick={(event) => {
                                    setAnchorEl(event.currentTarget);
                                    setSelectedRow(row);
                                  }}
                                >
                                  <MoreVertIcon />
                                </IconButton>
                                <Menu
                                  anchorEl={anchorEl}
                                  open={Boolean(anchorEl) && selectedRow?.id === id}
                                  onClose={handleMenuClose}
                                >
                                  <MenuItem onClick={handleViewMessage}>
                                    View Full Message
                                  </MenuItem>
                                  {(role === 'Admin' || accessArr?.includes('delete')) && (
                                    <MenuItem onClick={handleDeleteConfirm} sx={{ color: 'error.main' }}
                                    disabled={true}>
                                      Delete
                                    </MenuItem>
                                  )}
                                </Menu>
                              </TableCell>
                            </TableRow>
                          );
                        })}
                      
                      {emptyRows > 0 && (
                        <TableRow style={{ height: 53 * emptyRows }}>
                          <TableCell colSpan={7} />
                        </TableRow>
                      )}
                    </TableBody>

                    {isContactNotFound && (
                      <TableBody>
                        <TableRow>
                          <TableCell align="center" colSpan={7} sx={{ py: 3 }}>
                            <SearchNotFound searchQuery={filterName} />
                          </TableCell>
                        </TableRow>
                      </TableBody>
                    )}
                  </Table>
                </TableContainer>
              </Scrollbar>

              <TablePagination
                rowsPerPageOptions={[5, 10, 25]}
                component="div"
                count={contacts?.length || 0}
                rowsPerPage={rowsPerPage}
                page={page}
                onPageChange={handleChangePage}
                onRowsPerPageChange={handleChangeRowsPerPage}
              />
            </Card>
          </Container>

          {/* View Message Dialog */}
          <Dialog
            open={openDialog}
            onClose={() => {
              setOpenDialog(false);
              setViewDialogData(null);
            }}
            maxWidth="md"
            fullWidth
          >
            <DialogTitle>Contact Message Details</DialogTitle>
            <DialogContent>
              <Stack spacing={2} sx={{ mt: 1 }}>
                <Typography variant="subtitle1">
                  <strong>Name:</strong> {viewDialogData?.full_name || 'N/A'}
                </Typography>
                <Typography variant="subtitle1">
                  <strong>Email:</strong> {viewDialogData?.email || 'N/A'}
                </Typography>
                <Typography variant="subtitle1">
                  <strong>Subject:</strong> {viewDialogData?.subject || 'N/A'}
                </Typography>
                <Typography variant="subtitle1">
                  <strong>Date:</strong> {viewDialogData?.created_at || 'N/A'}
                </Typography>
                <Typography variant="subtitle1">
                  <strong>Message:</strong>
                </Typography>
                <Typography 
                  variant="body1" 
                  sx={{ 
                    backgroundColor: 'grey.100', 
                    p: 2, 
                    borderRadius: 1,
                    whiteSpace: 'pre-wrap',
                    minHeight: '100px'
                  }}
                >
                  {viewDialogData?.message || 'No message content'}
                </Typography>
              </Stack>
            </DialogContent>
            <DialogActions>
              <Button onClick={() => {
                setOpenDialog(false);
                setViewDialogData(null);
              }}>Close</Button>
            </DialogActions>
          </Dialog>

          {/* Delete Confirmation Dialog */}
          <Dialog
            open={openDeleteDialog}
            onClose={() => setOpenDeleteDialog(false)}
          >
            <DialogTitle>Confirm Deletion</DialogTitle>
            <DialogContent>
              <Typography>
                Are you sure you want to delete this contact message from{" "}
                <strong>{selectedRow?.full_name}</strong>?
              </Typography>
            </DialogContent>
            <DialogActions>
              <Button onClick={() => setOpenDeleteDialog(false)}>Cancel</Button>
              <Button onClick={handleDelete} color="error" variant="contained">
                Delete
              </Button>
            </DialogActions>
          </Dialog>

          <ToastContainer
            position="bottom-center"
            autoClose={3000}
            hideProgressBar
            newestOnTop={false}
            closeOnClick
            rtl={false}
            pauseOnFocusLoss
            draggable
            pauseOnHover
            theme="light"
          />
        </Page>
      )}
    </>
  );
};

export default ContactUsMessages;
